<script module lang="ts">
  export type TaskStep = "confirm" | "add_bank" | "receive_pay" | "payment_sent";
</script>
<script lang="ts">
  import type { BankMatch, BankAccount, StatuteBill, EasyJetPlusAnswer } from "./EdbSettlementTask/types";
  import SettlementStatus from "./EdbSettlementTask/elements/SettlementStatus.svelte";
  import RailsForm from "../components/Form.svelte";
  import Workflow from "../lib/workflow/Workflow.svelte";
  import Step from "../lib/workflow/Step.svelte";
  import Aside from "../lib/workflow/Aside.svelte";
  import Header from "./Settlement/elements/Header.svelte";
  import LoadingOverlay from "./Settlement/elements/LoadingOverlay.svelte";
  import ConfirmFees from "./EdbSettlementTask/steps/ConfirmFees.svelte";
  import BankChoice from "./Settlement/steps/BankChoice.svelte";
  import BankForm from "./Settlement/steps/BankForm.svelte";
  import BankMatchResult from "./Settlement/steps/BankMatchResult.svelte";
  import MarketingOptIn from "./EdbSettlementTask/steps/MarketingOptIn.svelte";
  import PaymentSteps from "./EdbSettlementTask/steps/PaymentSteps.svelte";
  import PaymentSent from "./EdbSettlementTask/steps/PaymentSent.svelte";
  import CannotAddBank from "./Settlement/steps/CannotAddBank.svelte";
  import IdRequired from "./Settlement/steps/IdRequired.svelte";

  let {
    action,
    errors,
    currentStep,
    clientName,
    payoutAmount,
    statuteBill,
    jlgMainPhone,
    blockAddingBank,
    bankMatch,
    existingBankAccounts,
    idRequiredUrl,
    easyJetPlusUrl,
    easyJetPlusAnswer,
    askForMarketing,
  }: {
    action: string;
    errors: { [key: string]: string } | null;
    currentStep: TaskStep;
    clientName: string;
    payoutAmount: number;
    statuteBill: StatuteBill;
    jlgMainPhone: string;
    blockAddingBank: boolean;
    bankMatch: BankMatch;
    existingBankAccounts: BankAccount[];
    idRequiredUrl: string | null;
    easyJetPlusUrl?: string;
    easyJetPlusAnswer: EasyJetPlusAnswer;
    askForMarketing: boolean;
  } = $props();

  let minStep = $derived(
    {
      confirm: 1,
      add_bank: 2,
      receive_pay: 3,
      payment_sent: 4,
    }[currentStep] || 0,
  );

  let anotherAccountExists = $derived(existingBankAccounts.length !== 0);

  // svelte-ignore state_referenced_locally
  let bankAccountChoice = $state<string | undefined>(existingBankAccounts[0]?.id);

  // svelte-ignore state_referenced_locally
  const reopened = minStep >= 3; // Intentionally based on initial, not updated, props

  const scrollToTop = () => document.querySelector("#task-modal .modal-box")?.scrollTo(0, 0);
</script>

<RailsForm {action} method="put">
  <Workflow state={{step: 0, totalSteps: 3, caption: "Overview"}} onchange={scrollToTop}>
    {#if reopened}
      <div class="-mx-4 -mt-4 mb-4 rounded-t-lg overflow-clip">
        <SettlementStatus {currentStep} {payoutAmount} />
      </div>
    {:else}
      <Header {minStep} amount={payoutAmount}/>
    {/if}
    <div class="ui-rhythm">
      {#if errors?.general}<div class="ui-error">{errors.general}</div>{/if}
      <Step state={{caption: "Preflight"}} disabled={!idRequiredUrl}>
        <IdRequired url={idRequiredUrl!} phone={jlgMainPhone} />
      </Step>
      <Step state={{step: 1}} disabled={minStep >= 2}>
        <ConfirmFees {payoutAmount} {statuteBill} />
      </Step>
      <Step state={{step: 2}} disabled={minStep >= 3 || !anotherAccountExists} path="bank-choice">
        <BankChoice bind:selectedOption={bankAccountChoice} {existingBankAccounts} {errors} />
      </Step>
      <Step state={{step: 2}} disabled={minStep >= 3 || !blockAddingBank}>
        <CannotAddBank phone={jlgMainPhone} back={anotherAccountExists} />
      </Step>
      <Step state={{step: 2}} disabled={minStep >= 3}>
        <BankForm {clientName} {jlgMainPhone} {errors} allowBack={anotherAccountExists} />
      </Step>
      <Step state={{step: 2}} disabled={minStep >= 3}>
        <BankMatchResult {bankMatch} phone={jlgMainPhone} backToBankChoice={anotherAccountExists} />
      </Step>
      <Step state={{step: 3}} disabled={!askForMarketing}>
        <MarketingOptIn {errors} />
      </Step>
      <Step state={{step: 3}} disabled={minStep >= 4}>
        <PaymentSteps {payoutAmount} {statuteBill} {bankMatch} {reopened} {easyJetPlusUrl} {easyJetPlusAnswer} />
      </Step>
      <Step disabled={minStep != 4}>
        <PaymentSent {payoutAmount} {statuteBill} {bankMatch} {easyJetPlusUrl} {easyJetPlusAnswer} />
      </Step>
    </div>

    <Aside name="name-matching">
      <LoadingOverlay
        title="Verifying bank account"
        description="For your security, we’re ensuring your bank account matches our records. This may take a few moments." />
    </Aside>
  </Workflow>
</RailsForm>
